import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { requestSuggestion } from "../../abstract/api/rest";
import * as nodeUtils from "../../abstract/controller/utils/nodeUtils";
import useRequestContainer from "../../abstract/hook/useRequestContainer";
import { getEndpointAI } from "../../abstract/redux/slice/endpointSlice";
import { handleChoiceUpdateValue } from "../../abstract/redux/slice/structureSlice";
import IconInfoCircle from "../../themes/icons/IconInfoCircle";
import TooltipIconValue from "../tooltip/TooltipIconValue";
import TooltipValue from "../tooltip/TooltipValue";
import * as vendorTooltip from "../ui/tooltips/DefaultTooltip";
import styles from "./ElementValue.module.scss";

const _placeholderSuggestion = "Loading suggestion...";
const _placeholderDefault = "Value";

function ElementValue({ node }) {
  const _dispatch = useDispatch();

  const _endpointAI = useSelector(getEndpointAI);

  const _id = nodeUtils.getID(node) ?? "";
  const _uuid = nodeUtils.getUUID(node) ?? "";
  const _value = nodeUtils.getValue(node) ?? "";

  const _container = useRequestContainer({ id: _id, nodeParent: node, type: "value" });

  const [_optionValue, _setOptionValue] = useState(_value);
  const [_isLoadingSuggestion, _setIsLoadingSuggestion] = useState(false);

  const [_placeholder, _setPlaceholder] = useState(_placeholderDefault);

  const _valueRef = useRef(null);
  const _suggestionTimeoutRef = useRef(null);
  const _abortRef = useRef(null);

  const clearSuggestionTimeout = useCallback(() => {
    if (_suggestionTimeoutRef.current) {
      clearTimeout(_suggestionTimeoutRef.current);
      _suggestionTimeoutRef.current = null;
    }

    _setIsLoadingSuggestion(false);
  }, []);

  const actionChangeValueAccept = useCallback(() => {
    clearSuggestionTimeout();

    _dispatch(handleChoiceUpdateValue(_id, _optionValue));
  }, [clearSuggestionTimeout, _dispatch, _id, _optionValue]);

  const actionChangeValueCancel = useCallback(() => {
    clearSuggestionTimeout();
    _setPlaceholder(_placeholderDefault);
  }, [clearSuggestionTimeout]);

  const handleInputValueOnChange = (e) => {
    const _value = e.target.value;

    _setOptionValue(_value);
    _setPlaceholder(_placeholderDefault);

    if (_value && _value.trim() !== "") {
      if (_valueRef.current.selectionEnd >= _valueRef.current.textLength) {
        suggestValueSuffix(_value);
      } else {
        clearSuggestionTimeout();
      }
    }
  };

  const handleInputValueFocusIn = () => {
    if (_isLoadingSuggestion) {
      return;
    }

    if (!_optionValue || _optionValue.trim() === "") {
      suggestValue();
    } else {
      _valueRef.current.select();
    }
  };

  const handleInputValueFocusOut = () => {
    if (_isLoadingSuggestion) {
      return;
    }

    clearSuggestionTimeout();

    if (_optionValue) {
      _dispatch(handleChoiceUpdateValue(_id, _optionValue));
    }
  };

  const suggestValue = useCallback(async () => {
    if (!_endpointAI) {
      return;
    }
    
    _setIsLoadingSuggestion(true);
    _setPlaceholder(_placeholderSuggestion);

    _abortRef.current = new AbortController();

    try {
      const _suggestion = await requestSuggestion({
        controller: _abortRef.current,
        ..._container.request,
      });

      if (_valueRef.current) {
        if (_suggestion.status !== "error" && !_valueRef.current.value) {
          _setOptionValue(_suggestion.message);

          _valueRef.current.value = _suggestion.message;
          _valueRef.current.select();
        } else {
          _setPlaceholder("No available suggestions");
        }
      }
    } catch (exception) {}

    clearSuggestionTimeout();
  }, [_container.request, _endpointAI, clearSuggestionTimeout]);

  const suggestValueSuffix = useCallback(
    async (prefix) => {
      if (!_endpointAI) {
        return;
      }
      if (_suggestionTimeoutRef.current) {
        return;
      }

      _abortRef.current = new AbortController();

      _suggestionTimeoutRef.current = setTimeout(async () => {
        _setIsLoadingSuggestion(true);

        try {
          const _suggestion = await requestSuggestion({
            controller: _abortRef.current,
            prefix,
            ..._container.request,
          });

          if (_suggestion.status !== "error") {
            _setOptionValue(_suggestion.message);

            if (_valueRef.current) {
              _valueRef.current.value = _suggestion.message;
              _valueRef.current.setSelectionRange(prefix.length, _suggestion.message.length);
            }
          } else {
            _setPlaceholder("No available suggestions");
          }
        } catch (error) {}

        clearSuggestionTimeout();
      }, 2000);
    },
    [_container.request, _endpointAI, clearSuggestionTimeout]
  );

  useEffect(() => {
    const _target = _valueRef.current;

    const callback = (e) => {
      if (e.code === "Enter") {
        if (!_isLoadingSuggestion) {
          actionChangeValueAccept();
        }
      } else if (e.code === "Escape") {
        actionChangeValueCancel();
      } else if (e.code === "ArrowRight") {
        if (_target.selectionEnd + 1 >= _target.textLength) {
          suggestValueSuffix(_target.defaultValue);
        }
      } else {
        clearSuggestionTimeout();
      }
    };

    _target?.addEventListener("keydown", callback);

    return () => _target?.removeEventListener("keydown", callback);
  }, [
    _isLoadingSuggestion,
    actionChangeValueAccept,
    actionChangeValueCancel,
    clearSuggestionTimeout,
    suggestValueSuffix,
  ]);

  return (
    <div className={styles["meta__option"]}>
      <div className={styles["meta__option-header"]}>
        <div className={styles["meta__option-header-name"]}>Value</div>
        <div className={styles["meta__option-header-icon"]}>
          <IconInfoCircle
            data-tooltip-id={`meta-tooltip-value-${_uuid}`}
            data-tooltip-delay-show={vendorTooltip.getTimeFast()}
          />
          <TooltipIconValue handle={`meta-tooltip-value-${_uuid}`} />
        </div>
      </div>
      <input
        ref={_valueRef}
        type="text"
        value={_optionValue}
        onChange={handleInputValueOnChange}
        onFocus={handleInputValueFocusIn}
        onBlur={handleInputValueFocusOut}
        className={`${styles["meta__option-input"]} ${_isLoadingSuggestion ? styles.shimmer : ""}`}
        data-title={_optionValue}
        data-tooltip-id={`meta-value-${_uuid}`}
        data-tooltip-delay-show={vendorTooltip.getTimeFast()}
        placeholder={_placeholder}
      />
      <TooltipValue node={node} handle={`meta-value-${_uuid}`} />
    </div>
  );
}

export default ElementValue;
